import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredProducts } from '../../../mocks/pharmacy';
import { useCart } from '@/hooks/useCart';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const INITIAL_COUNT = 6;

const FeaturedProducts = () => {
  const [activeFilter, setActiveFilter] = useState('ВСЕ');
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const { addItem, hasItem } = useCart();
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal();

  const filters = ['ВСЕ', 'СКИДКИ', 'НОВИНКИ', 'ТОП'];

  const filteredProducts = featuredProducts.filter((p) => {
    if (activeFilter === 'ВСЕ') return true;
    if (activeFilter === 'СКИДКИ') return p.badge.startsWith('-');
    if (activeFilter === 'НОВИНКИ') return p.badge === 'НОВИНКА';
    if (activeFilter === 'ТОП') return p.badge === 'ХИТ';
    return true;
  });

  const visibleProducts = showAll ? filteredProducts : filteredProducts.slice(0, INITIAL_COUNT);

  // resetKey меняется при смене фильтра или раскрытии — анимация переигрывается
  const resetKey = useMemo(
    () => `${activeFilter}-${showAll}-${visibleProducts.length}`,
    [activeFilter, showAll, visibleProducts.length],
  );

  const { containerRef: gridRef, visibleItems } = useStaggerReveal(
    visibleProducts.length,
    { threshold: 0.05 },
    resetKey,
  );

  const handleAddToCart = (product: typeof featuredProducts[0]) => {
    if (!hasItem(product.id)) {
      addItem({ id: product.id, name: product.name, price: product.price, oldPrice: product.oldPrice, image: product.image, color: product.color });
    }
  };

  return (
    <section className="py-20 px-6 lg:px-12 relative" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6 reveal-hidden ${headerVisible ? 'reveal-up' : ''}`}
        >
          <div>
            <h2 className="font-orbitron font-black">
              <span className="block text-3xl md:text-5xl text-white">ПОПУЛЯРНЫЕ</span>
              <span
                className="block text-3xl md:text-5xl text-neon-green"
                style={{ textShadow: '0 0 20px #39ff14, 0 0 50px #39ff14' }}
              >
                ТОВАРЫ
              </span>
            </h2>
          </div>

          {/* Filters */}
          <div
            className="flex gap-1 p-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => { setActiveFilter(f); setShowAll(false); }}
                className="whitespace-nowrap px-4 py-2 rounded-full font-mono text-xs tracking-wider transition-all duration-300 cursor-pointer"
                style={
                  activeFilter === f
                    ? { background: '#ff2d78', color: '#fff', boxShadow: '0 0 15px #ff2d78', textShadow: '0 0 8px rgba(255,255,255,0.8)' }
                    : { color: 'rgba(255,255,255,0.4)' }
                }
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {visibleProducts.map((product, idx) => (
            <div
              key={product.id}
              onClick={() => navigate(`/product/${product.id}`)}
              className="card-cyber rounded-2xl overflow-hidden group relative cursor-pointer"
              style={{
                borderColor: `${product.color}20`,
                opacity: visibleItems[idx] ? 1 : 0,
                transform: visibleItems[idx] ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.97)',
                transition: `opacity 0.5s cubic-bezier(0.22,1,0.36,1) ${(idx % 3) * 70}ms, transform 0.5s cubic-bezier(0.22,1,0.36,1) ${(idx % 3) * 70}ms`,
              }}
            >
              {/* Badge */}
              <div
                className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full font-orbitron font-bold text-xs tracking-wider"
                style={{ background: product.badgeColor, color: '#050508', boxShadow: `0 0 10px ${product.badgeColor}` }}
              >
                {product.badge}
              </div>

              {/* Image Area */}
              <div
                className="relative h-56 flex items-center justify-center overflow-hidden"
                style={{ background: `linear-gradient(135deg, #0a0a14 0%, ${product.color}08 100%)` }}
              >
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-16 rounded-full opacity-30 group-hover:opacity-60 transition-opacity"
                  style={{ background: product.color, filter: 'blur(20px)' }}
                />
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-40 h-40 object-contain object-center relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-2 animate-float"
                  style={{ filter: `drop-shadow(0 10px 20px ${product.color}60)`, animationDelay: `${product.id * 0.3}s` }}
                />
                <div className="absolute inset-0 scanline opacity-20" />
                <div className="absolute top-2 right-2 w-5 h-5" style={{ borderTop: `1px solid ${product.color}`, borderRight: `1px solid ${product.color}` }} />
                <div className="absolute bottom-2 left-2 w-5 h-5" style={{ borderBottom: `1px solid ${product.color}`, borderLeft: `1px solid ${product.color}` }} />
              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 pr-2">
                    <h3 className="font-orbitron font-bold text-sm text-white group-hover:text-white/90 leading-tight">
                      {product.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <i className="ri-star-fill text-xs" style={{ color: '#f0ff00' }} />
                    <span className="font-mono text-xs text-white/60">{product.rating}</span>
                  </div>
                </div>

                <p className="font-mono text-xs text-white/40 mb-4 leading-relaxed line-clamp-2">
                  {product.description}
                </p>

                {/* Price Row */}
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span
                      className="font-orbitron font-black text-xl"
                      style={{ color: product.color, textShadow: `0 0 10px ${product.color}` }}
                    >
                      {product.price.toLocaleString()}₽
                    </span>
                    <span className="font-mono text-xs text-white/30 line-through">
                      {product.oldPrice.toLocaleString()}₽
                    </span>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                    className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-full cursor-pointer font-mono text-xs tracking-wider transition-all duration-300"
                    style={
                      hasItem(product.id)
                        ? { background: product.color, color: '#050508', boxShadow: `0 0 15px ${product.color}` }
                        : { border: `1px solid ${product.color}`, color: product.color, boxShadow: `0 0 8px ${product.color}30` }
                    }
                  >
                    <i className={hasItem(product.id) ? 'ri-check-line' : 'ri-shopping-bag-line'} />
                    <span>{hasItem(product.id) ? 'В КОРЗИНЕ' : 'В КОРЗИНУ'}</span>
                  </button>
                </div>

                {/* Reviews */}
                <div
                  className="mt-3 pt-3 flex items-center gap-1"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className={`text-[10px] ${s <= Math.round(product.rating) ? 'ri-star-fill' : 'ri-star-line'}`} style={{ color: '#f0ff00' }} />
                    ))}
                  </div>
                  <span className="font-mono text-[10px] text-white/30 ml-1">{product.reviews} отзывов</span>
                </div>
              </div>

              {/* Top border accent */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `linear-gradient(90deg, transparent, ${product.color}, transparent)` }}
              />
            </div>
          ))}
        </div>

        {/* Show More / Less */}
        {filteredProducts.length > INITIAL_COUNT && (
          <div className="mt-12 text-center">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="whitespace-nowrap btn-neon-pink px-10 py-4 rounded-full font-orbitron font-bold text-sm tracking-widest cursor-pointer"
            >
              {showAll ? `СВЕРНУТЬ  ` : `ПОКАЗАТЬ ЕЩЁ (${filteredProducts.length - INITIAL_COUNT})  `}
              <i className={showAll ? 'ri-arrow-up-line ml-2' : 'ri-arrow-down-line ml-2'} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
